/**
 * "Open Apiboo when you sign in to your computer" (owner, 05.10.2026, 1.0.6).
 * Rust owns the entry (src-tauri/src/autostart.rs); a sign-in start stays in
 * the tray.
 *
 * The default (on) is applied ONCE per device, on the first start of a build
 * that has the setting (new install or update). After that only the user's
 * toggle in Settings writes it, so switching Apiboo off in the system's own
 * startup list (Windows Task Manager › Startup apps, macOS Login Items) is
 * respected and never undone on the next start — which is what well-behaved
 * apps do (Discord, Slack, Docker Desktop ask once, then follow the system).
 * Settings shows the REAL state, read from the system.
 */
import { isTauri } from '../utils/tauri';

async function invokeCmd(cmd, args) {
  const { invoke } = await import('@tauri-apps/api/core');
  return invoke(cmd, args);
}

/** Whether Apiboo opens at sign-in right now (false outside the desktop app). */
export async function getLaunchAtLogin() {
  if (!isTauri()) return false;
  try {
    return Boolean(await invokeCmd('launch_at_login_status'));
  } catch {
    return false;
  }
}

/** Switch it on or off; resolves to the state afterwards. Throws a sentence on failure. */
export async function setLaunchAtLogin(enabled) {
  if (!isTauri()) return false;
  return Boolean(await invokeCmd('set_launch_at_login', { enabled: Boolean(enabled) }));
}

/**
 * First start of a build with the setting: turn it on and remember that the
 * default was applied. `prefs` is the hydrated preferences, `markApplied`
 * persists `launchAtLoginApplied: true`. Never throws.
 */
export async function applyLaunchAtLoginDefaultOnce(prefs, markApplied) {
  if (!isTauri() || prefs?.launchAtLoginApplied === true) return;
  try {
    await setLaunchAtLogin(true);
  } catch {
    // Not fatal: the toggle in Settings shows the real state and can retry.
  }
  markApplied();
}
